import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  PlusIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { staffApi } from '../../api/staffApi';

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentStaffId, setCurrentStaffId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    role: 'cashier',
    permissions: []
  });

  const availablePermissions = [
    { id: 'dashboard', label: 'Dashboard (View Analytics)' },
    { id: 'customers', label: 'Customers (View & Add)' },
    { id: 'transactions', label: 'Transactions (Add & Edit)' },
    { id: 'inventory', label: 'Inventory (Products)' },
    { id: 'reports', label: 'Reports (Export)' },
    { id: 'payments', label: 'Payments' },
    { id: 'staff', label: 'Staff Management' },
    { id: 'settings', label: 'Settings' }
  ];

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await staffApi.getStaff();
      setStaffList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({ name: '', phone: '', email: '', password: '', role: 'cashier', permissions: ['customers', 'transactions'] });
    setIsModalOpen(true);
  };

  const openEditModal = (staff) => {
    setIsEditMode(true);
    setCurrentStaffId(staff._id);
    setFormData({
      name: staff.name,
      phone: staff.phone,
      email: staff.email,
      password: '', // Leave blank for edit unless they want to change it
      role: staff.role,
      permissions: staff.permissions || []
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        // If password is empty in edit mode, don't send it
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await staffApi.updateStaff(currentStaffId, updateData);
      } else {
        await staffApi.addStaff(formData);
      }
      setIsModalOpen(false);
      fetchStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      alert(error.response?.data?.message || 'Error saving staff');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this staff member?')) {
      try {
        await staffApi.deleteStaff(id);
        fetchStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
      }
    }
  };

  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Staff Management</h1>
          <p className="text-[15px] font-medium text-gray-500 dark:text-gray-400 mt-1">
            Manage your cashiers and managers, assign roles.
          </p>
        </div>
        <button 
          onClick={openAddModal}
          className="bg-[#dc343b] text-white px-5 py-2.5 rounded-xl font-bold text-[14px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20 flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Staff
        </button>
      </div>

      {/* Staff List */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
        {loading ? (
          <div className="p-10 text-center text-gray-500 font-medium text-sm">Loading staff...</div>
        ) : staffList.length === 0 ? (
          <div className="p-10 text-center flex flex-col items-center">
            <UsersIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Staff Members Found</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Add cashiers to help you manage transactions.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {staffList.map((staff) => (
              <div key={staff._id} className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500 font-bold text-[15px] shadow-sm shrink-0">
                    {staff.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-gray-900 dark:text-white">{staff.name}</h3>
                    <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{staff.phone} • {staff.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[11px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {staff.role}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => openEditModal(staff)}
                    className="p-2 rounded-xl text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => handleDelete(staff._id)}
                    className="p-2 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-xl border border-gray-100 dark:border-gray-700 transition-colors">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              {isEditMode ? 'Edit Staff Member' : 'Add New Staff'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 px-4 text-gray-900 dark:text-white text-[14px] outline-none focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] transition-all"
                  placeholder="e.g. Rahul Kumar"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 px-4 text-gray-900 dark:text-white text-[14px] outline-none focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] transition-all"
                  placeholder="e.g. 9876543210"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1">Email (Optional)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 px-4 text-gray-900 dark:text-white text-[14px] outline-none focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] transition-all"
                  placeholder="rahul@example.com"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1">
                  Password {isEditMode && '(Leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!isEditMode}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 px-4 text-gray-900 dark:text-white text-[14px] outline-none focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] transition-all"
                  placeholder="Enter secure password"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2.5 px-4 text-gray-900 dark:text-white text-[14px] outline-none focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] transition-all"
                >
                  <option value="cashier">Cashier</option>
                  <option value="manager">Manager</option>
                </select>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-600 dark:text-gray-400 mb-2">Custom Permissions</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  {availablePermissions.map(perm => (
                    <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.permissions.includes(perm.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, permissions: [...formData.permissions, perm.id] });
                          } else {
                            setFormData({ ...formData, permissions: formData.permissions.filter(p => p !== perm.id) });
                          }
                        }}
                        className="rounded text-[#dc343b] focus:ring-[#dc343b] w-4 h-4"
                      />
                      <span className="text-[13px] text-gray-700 dark:text-gray-300">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-[14px] hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-[#dc343b] text-white rounded-xl font-bold text-[14px] hover:bg-[#b91c1c] transition-colors shadow-sm"
                >
                  {isEditMode ? 'Save Changes' : 'Add Staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
